import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Link,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import Layout from "../../components/layout/Layout";
import Panel from "../../components/brand/Panel";
import KpiTile from "../../components/brand/KpiTile";
import { fonts } from "../../theme/tokens";
import { getFinanceDashboard } from "../../api/financeApi";
import { formatDateUk } from "../../utils/dateDisplay";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function FinanceDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [debtorsPage, setDebtorsPage] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getFinanceDashboard(debtorsPage);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load finance dashboard.");
    } finally {
      setLoading(false);
    }
  }, [debtorsPage]);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = data?.metrics || {};
  const debtors = data?.debtors || { results: [], total_pages: 1, total: 0 };

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", letterSpacing: 1.2, fontFamily: fonts.mono }}
        >
          Finance
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
          Finance dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Budget renewals, collections, spend and arrears across your properties.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(data?.recalculations_needed || []).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {(data.recalculations_needed || []).length} propert
          {(data.recalculations_needed || []).length === 1 ? "y" : "ies"} need service charge
          recalculation after budget or apportionment changes.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {loading ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={100} />)
        ) : (
          <>
            <KpiTile label="Budget total (YTD)" value={money(metrics.budget_total)} />
            <KpiTile label="Collected (YTD)" value={money(metrics.collected_ytd)} />
            <KpiTile label="Spent (YTD)" value={money(metrics.spent_ytd)} />
            <KpiTile label="Outstanding" value={money(metrics.outstanding_ytd)} />
          </>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2, mb: 3 }}>
        <Panel title="Budgets due for renewal (90 days)" noBodyPadding={false}>
          {loading ? (
            <Skeleton height={120} />
          ) : (data?.renewals_due || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No draft budgets approaching fiscal year end.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Budget</TableCell>
                    <TableCell align="right">Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.renewals_due || []).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/properties/${row.property_id}/finance?tab=budgets`}
                        >
                          {row.property_name}
                        </Link>
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">{row.days_to_renewal}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Panel>

        <Panel title="Pending approval" noBodyPadding={false}>
          {loading ? (
            <Skeleton height={120} />
          ) : (data?.pending_approval || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No draft budgets awaiting approval.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Budget</TableCell>
                    <TableCell>FY end</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.pending_approval || []).slice(0, 10).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/properties/${row.property_id}/finance?tab=budgets`}
                        >
                          {row.property_name}
                        </Link>
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{formatDateUk(row.fiscal_year_end)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Panel>
      </Box>

      {(data?.recalculations_needed || []).length > 0 && (
        <Panel title="Service charge recalculation needed" noBodyPadding={false} sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Property</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.recalculations_needed || []).map((row) => (
                  <TableRow key={row.property_id} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/properties/${row.property_id}/finance?tab=overview`}
                      >
                        {row.property_name}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "pre-line" }}>{row.reason}</TableCell>
                    <TableCell>{formatDateUk(row.updated_at_utc?.slice?.(0, 10))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      )}

      <Panel
        title="Debtors (3+ months arrears)"
        noBodyPadding={false}
        right={
          <Button
            size="small"
            startIcon={<AccountBalanceWalletIcon />}
            onClick={() => navigate("/properties")}
          >
            View properties
          </Button>
        }
      >
        {loading ? (
          <Skeleton height={200} />
        ) : debtors.results.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No units in arrears over three months.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right">Days overdue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debtors.results.map((row) => (
                    <TableRow key={`${row.property_id}-${row.unit_id}`} hover>
                      <TableCell>{row.property_name}</TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/units/${row.unit_id}/statement`}
                        >
                          {row.unit_name}
                        </Link>
                      </TableCell>
                      <TableCell>{row.owner_name || "—"}</TableCell>
                      <TableCell align="right">{money(row.outstanding)}</TableCell>
                      <TableCell align="right">{row.days_overdue}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {debtors.total_pages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={debtors.total_pages}
                  page={debtorsPage}
                  onChange={(_, p) => setDebtorsPage(p)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Panel>
    </Layout>
  );
}
